import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

const DN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SETS = { mon_sat: [0, 1, 2, 3, 4, 5], mon_fri: [0, 1, 2, 3, 4], every: [0, 1, 2, 3, 4, 5, 6] };
const SET_LABEL = { mon_sat: "Monday to Saturday", mon_fri: "Monday to Friday", every: "Every day" };
const PER_DAY = ["Once a day", "Twice a day", "3 times a day"];
const sel = "h-8 border bg-white px-1.5 text-xs rounded-sm disabled:bg-muted/40 disabled:text-muted-foreground";
const GRID = "md:grid md:grid-cols-[minmax(0,1.6fr)_64px_118px_minmax(0,1.2fr)_minmax(0,1.7fr)] md:gap-3";
const btn = "h-9 px-4 text-sm disabled:opacity-50 transition-colors";

export const tLabel = (s) => { const h = +s.slice(0, 2), m = +s.slice(3); return `${h % 12 || 12}${m ? `:${s.slice(3)}` : ""} ${h < 12 ? "AM" : "PM"}`; };
const sorted = (a) => [...a].sort((x, y) => (x > y ? 1 : -1)).join();
const presetOf = (days) => Object.keys(SETS).find((k) => SETS[k].join() === sorted(days)) || "custom";
const same = (a, b) => !!a && !!b && a.on === b.on && sorted(a.times) === sorted(b.times) && sorted(a.days) === sorted(b.days);
const dupTimes = (s) => new Set(s.times).size !== s.times.length;

const resize = (times, n, slots) => {
  if (n <= times.length) return times.slice(0, n);
  const out = [...times];
  while (out.length < n) {
    const last = slots.indexOf(out[out.length - 1]);
    out.push(slots.find((s, i) => i > last + 3 && !out.includes(s)) || slots.find((s) => !out.includes(s)));
  }
  return out;
};

const Head = () => (
  <div className={`hidden ${GRID} text-[11px] uppercase tracking-wide text-muted-foreground border-b pb-1.5 mt-3`}>
    <span>Email</span><span>On</span><span>Per day</span><span>Times</span><span>Days</span>
  </div>
);

const DayPicker = ({ s, k, tid, disabled, set }) => {
  const preset = presetOf(s.days);
  const flip = (i) => { const d = s.days.includes(i) ? s.days.filter((x) => x !== i) : [...s.days, i]; if (d.length) set({ days: d.sort() }); };
  return (
    <div className="space-y-1.5">
      <select className={sel} disabled={disabled} data-testid={`${tid}-days-${k}`} value={preset} onChange={(e) => SETS[e.target.value] && set({ days: SETS[e.target.value] })}>
        {Object.entries(SET_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        {preset === "custom" && <option value="custom">Picked days</option>}
      </select>
      <div className="flex flex-wrap gap-1">
        {DN.map((d, i) => (
          <button key={d} type="button" disabled={disabled} data-testid={`${tid}-day-${k}-${i}`} aria-pressed={s.days.includes(i)} onClick={() => flip(i)}
            className={`text-[11px] h-6 px-1.5 rounded-sm border transition-colors ${s.days.includes(i) ? "bg-violet-600 border-violet-600 text-white" : "bg-white text-slate-600 hover:border-violet-400"} disabled:opacity-60`}>{d}</button>
        ))}
      </div>
    </div>
  );
};

const SchedRow = ({ meta, s, slots, onChange, disabled, tid, note }) => {
  const set = (patch) => onChange({ ...s, ...patch });
  const k = meta.key;
  return (
    <div className={`${GRID} space-y-2 md:space-y-0 items-start py-3 border-b last:border-0`} data-testid={`${tid}-row-${k}`}>
      <div><div className="text-sm font-medium">{meta.label}</div><div className="text-[11px] text-muted-foreground">{meta.who}</div>{note}</div>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={s.on} disabled={disabled} onChange={(e) => set({ on: e.target.checked })} data-testid={`${tid}-on-${k}`} />{s.on ? "On" : "Off"}</label>
      <div>{meta.max > 1 ? (
        <select className={sel} disabled={disabled} data-testid={`${tid}-per-day-${k}`} value={s.times.length} onChange={(e) => set({ times: resize(s.times, +e.target.value, slots) })}>
          {PER_DAY.slice(0, meta.max).map((l, i) => <option key={l} value={i + 1}>{l}</option>)}
        </select>) : <span className="text-xs text-muted-foreground">Once a day</span>}</div>
      <div className="flex flex-wrap gap-1.5">{s.times.map((t, i) => (
        <select key={`${k}-${i}`} className={sel} disabled={disabled} data-testid={`${tid}-time-${k}-${i}`} value={t} onChange={(e) => set({ times: s.times.map((x, j) => (j === i ? e.target.value : x)) })}>
          {slots.map((o) => <option key={o} value={o}>{tLabel(o)}</option>)}
        </select>))}</div>
      <DayPicker s={s} k={k} tid={tid} disabled={disabled} set={set} />
    </div>
  );
};

const TeamSchedule = ({ d, onSaved }) => {
  const [team, setTeam] = useState(d.team);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTeam(d.team); }, [d.team]);
  const dirty = d.kinds.some((k) => !same(team[k.key], d.team[k.key]));
  const save = () => {
    if (Object.values(team).some(dupTimes)) { toast.error("Pick a different time for each send of the same email"); return; }
    setBusy(true);
    api.put("/admin/reminder-schedule/team", { team }).then((r) => { onSaved(r.data.team); toast.success("Team schedule saved"); })
      .catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  return (
    <section className="ax-panel p-4 mb-4" data-testid="reminder-team-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1"><div className="font-medium text-sm">Email reminders · team schedule</div>
          <p className="text-xs text-muted-foreground">When each reminder email goes out for everyone, unless someone has their own schedule below. India time; emails arrive within about 15 minutes of the time set.</p></div>
        <button type="button" data-testid="reminder-team-save" disabled={!dirty || busy} onClick={save} className={`${btn} bg-primary text-primary-foreground`}>Save team schedule</button>
      </div>
      <Head />
      {d.kinds.map((k) => <SchedRow key={k.key} meta={k} s={team[k.key]} slots={d.slots} tid="reminder-team" onChange={(s) => setTeam({ ...team, [k.key]: s })} />)}
    </section>
  );
};

const ModeNote = ({ k, mine, differs, onMode }) => (
  <div className="mt-1.5 flex flex-wrap items-center gap-2">
    <select className={sel} data-testid={`reminder-person-mode-${k}`} value={mine ? "own" : "team"} onChange={(e) => onMode(e.target.value)}>
      <option value="team">Team schedule</option><option value="own">Own schedule</option>
    </select>
    {differs && <span data-testid={`reminder-person-differs-${k}`} className="text-[11px] font-medium text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">Differs from team</span>}
  </div>
);

const PersonRows = ({ d, u, own, setOwn }) => d.kinds.filter((k) => u.gets.includes(k.key)).map((k) => {
  const mine = own[k.key];
  const differs = !!mine && !same(mine, d.team[k.key]);
  const onMode = (v) => { const n = { ...own }; if (v === "own") n[k.key] = { ...d.team[k.key] }; else delete n[k.key]; setOwn(n); };
  return (
    <div key={k.key} className={differs ? "bg-amber-50 -mx-2 px-2 rounded-sm" : ""}>
      <SchedRow meta={k} s={mine || d.team[k.key]} slots={d.slots} tid="reminder-person" disabled={!mine} onChange={(s) => setOwn({ ...own, [k.key]: s })}
        note={<ModeNote k={k.key} mine={mine} differs={differs} onMode={onMode} />} />
    </div>
  );
});

const OwnList = ({ d, pick }) => {
  const rows = d.users.filter((x) => Object.keys(d.people[x.user_id] || {}).length);
  if (!rows.length) return <p className="text-xs text-muted-foreground mb-2" data-testid="reminder-own-none">Everyone follows the team schedule.</p>;
  return (
    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="reminder-own-list">
      {rows.map((x) => (
        <button key={x.user_id} type="button" data-testid={`reminder-own-${x.user_id}`} onClick={() => pick(x.user_id)}
          className="text-xs rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 px-2.5 py-1 transition-colors">{x.name} · {Object.keys(d.people[x.user_id]).length} own</button>
      ))}
    </div>
  );
};

const PersonSchedule = ({ d, onSaved }) => {
  const [uid, setUid] = useState("");
  const [own, setOwn] = useState({});
  const [busy, setBusy] = useState(false);
  const u = d.users.find((x) => x.user_id === uid);
  const saved = d.people[uid] || {};
  useEffect(() => { setOwn({ ...(d.people[uid] || {}) }); }, [uid, d.people]);
  const dirty = sorted(Object.keys(own)) !== sorted(Object.keys(saved)) || Object.keys(own).some((k) => !same(own[k], saved[k]));
  const done = (r, msg) => { onSaved(uid, r.data.own); toast.success(msg); };
  const save = () => {
    if (Object.values(own).some(dupTimes)) { toast.error("Pick a different time for each send of the same email"); return; }
    setBusy(true);
    api.put(`/admin/reminder-schedule/person/${uid}`, { kinds: Object.fromEntries(u.gets.map((k) => [k, own[k] || null])) })
      .then((r) => done(r, `${u.name}'s schedule saved`)).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  const reset = () => api.delete(`/admin/reminder-schedule/person/${uid}`).then((r) => done(r, `${u.name} is back on the team schedule`)).catch((e) => toast.error(errMsg(e)));
  return (
    <section className="ax-panel p-4 mb-4" data-testid="reminder-person-panel">
      <div className="font-medium text-sm">Email reminders · one person</div>
      <p className="text-xs text-muted-foreground mb-2">Keep the team schedule, or give one person their own on/off, times, days and per-day count for any email.</p>
      <OwnList d={d} pick={setUid} />
      <select className={`${sel} h-9 w-full sm:w-96`} data-testid="reminder-person-select" value={uid} onChange={(e) => setUid(e.target.value)}>
        <option value="">Pick a person…</option>
        {d.users.map((x) => <option key={x.user_id} value={x.user_id}>{x.name} · {x.role}{x.own.length ? " · own schedule" : ""}</option>)}
      </select>
      {u && (
        <div data-testid="reminder-person-editor">
          <Head />
          <PersonRows d={d} u={u} own={own} setOwn={setOwn} />
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" data-testid="reminder-person-save" disabled={!dirty || busy} onClick={save} className={`${btn} bg-primary text-primary-foreground`}>Save {u.name.split(" ")[0]}'s schedule</button>
            {Object.keys(saved).length > 0 && <button type="button" data-testid="reminder-person-reset" onClick={reset} className={`${btn} border hover:border-primary`}>Back to team schedule</button>}
          </div>
        </div>
      )}
    </section>
  );
};

export const ReminderSchedule = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/reminder-schedule").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const on = (e) => setD((x) => (x ? { ...x, master_on: e.detail } : x));
    window.addEventListener("reminder-master", on);
    return () => window.removeEventListener("reminder-master", on);
  }, []);
  if (!d) return null;
  const savePerson = (uid, own) => setD((x) => {
    const people = { ...x.people };
    if (Object.keys(own).length) people[uid] = own; else delete people[uid];
    return { ...x, people, users: x.users.map((u) => (u.user_id === uid ? { ...u, own: Object.keys(own) } : u)) };
  });
  return (
    <div data-testid="reminder-schedule">
      {!d.master_on && <p className="text-xs text-amber-800 mb-2" data-testid="reminder-master-off">Reminder emails are switched off above, so none of these go out until they're switched back on.</p>}
      <TeamSchedule d={d} onSaved={(team) => setD((x) => ({ ...x, team }))} />
      <PersonSchedule d={d} onSaved={savePerson} />
    </div>
  );
};
